import React, { useEffect } from "react";
import { connect } from "react-redux";
import ICompany from "../../models/ICompany";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import { selectCompany } from "../../store/context/selectors";
import { Grid, Button, FormControlLabel, Checkbox, TextField } from "@material-ui/core";
import { withRouter, RouteComponentProps } from "react-router";
import snackbarService from "../../services/ui-messaging/SnackbarService";
import styled from "styled-components";
import Services from "../../services/Services";
import { FormErrors } from "../forms/FormErrors";
import validateForm from "../forms/validateForm";
import FormTextField from "../forms/FormTextField";
import createInputHandlers from "../forms/inputHandlers";
import { IPendingItem } from "../../store/status/types";
import { selectStatusDispatch } from "../../store/status/selectors";
import IContactForm from "../../models/IContactForm";
import IContact from "../../models/IContact";
import IFieldSet from "../../models/IFieldSet";

const ActionGrid = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const ActionButton = styled(Button)`
  margin: 0 10px;
`;

const pendingForm: IPendingItem = {
  id: "8a75a42f-73fb-450e-b73e-26753e4d5f28",
  description: "Adding new user",
};

interface MatchParams {
  id?: string;
}

export interface IContactAddProps extends RouteComponentProps<MatchParams> {
  token?: string;
  company?: ICompany;
  returnUrl?: string;
  addPending: Function;
  completePending: Function;
  id?: string;
}

const newFormErrors = (): FormErrors<IContactForm> => {
  return {
    first: {},
    last: {},
    title: {},
    email: {},
    phone: {},
    support: {},
    contactManager: {},
  };
};

const requiredFields = ["first", "last", "email", "title", "phone"];

const ContactAdd = (props: IContactAddProps) => {
  let { company, returnUrl, history, token, addPending, completePending, id } = props;

  const [form, setForm] = React.useState<IContactForm>({
    id: undefined,
    companyId: company!.ID,
    siteId: company!.DefaultSiteID,
    first: undefined,
    last: undefined,
    title: undefined,
    email: undefined,
    phone: undefined,
    support: false,
    contactManager: false,
    partnerPortalUser: false,
  });

  const [formErrors, setFormErrors] = React.useState<FormErrors<IContactForm>>(newFormErrors());

  const [isUpdate, setIsUpdate] = React.useState<boolean>(id ? true : false);

  useEffect(() => {
    const getContact = async (id: string) => {
      const contactRepo = await new Services().ContactRepository();
      const contactId = parseInt(id, 10);
      try {
        if (!token) {
          throw new Error("Unauthorized");
        }

        if (!company) {
          throw new Error("Unable to retrieve contact. Company not valid.");
        }

        var resp = await contactRepo.getByID(token, company.ID, contactId);

        if (resp) {
          let _form = {
            ...form,
            id: resp.ID,
            first: resp.FirstName,
            last: resp.LastName,
            title: resp.Title || "",
            email: resp.EmailAddress || "",
            phone: resp.PhoneNumber || "",
            support: resp.SupportContact,
            contactManager: resp.SupportContactManager,
            partnerPortalUser: resp.PartnerPortalUser,
          };

          setForm(_form);
        }
      } catch (err) {
        snackbarService.error(err);
      }
    };

    const paramId = props.match.params.id;
    if (paramId) {
      setIsUpdate(true);
      getContact(paramId);
    }
  }, []);

  if (!returnUrl) {
    returnUrl = "/users/list";
  } else {
    returnUrl = returnUrl.replace(/['"]+/g, "");
  }

  const getFields = (): IFieldSet<IContact>[] => {
    let fields: IFieldSet<IContact>[] = [];
    if (form.first && form.last && form.email && form.title && form.phone) {
      fields = [
        {
          name: "FirstName",
          value: form.first,
        },
        {
          name: "LastName",
          value: form.last,
        },
        {
          name: "EmailAddress",
          value: form.email,
        },
        {
          name: "Title",
          value: form.title,
        },
        {
          name: "PhoneNumber",
          value: form.phone,
        },
        {
          name: "SupportContact",
          value: form.support!.toString() || false.toString(),
        },
        {
          name: "SupportContactManager",
          value: form.contactManager!.toString() || false.toString(),
        },
        {
          name: "PartnerPortalUser",
          value: form.partnerPortalUser!.toString() || false.toString(),
        },
      ];
    }
    return fields;
  };

  const validate = () => {
    validateForm(form, {
      requiredFields,
      formErrors: newFormErrors(),
      onFormError: setFormErrors,
    });
  };

  const { handleChange, handleCheck } = createInputHandlers(form, setForm);

  const handleCancel = () => {
    history.goBack();
    snackbarService.info(`User ${isUpdate ? "Update" : "Creation"} Cancelled`);
  };

  const handleSubmit = async () => {
    let pendForm = {
      ...pendingForm,
    };

    try {
      if (!token) {
        throw new Error("Unauthorized");
      }

      validate();
      if (isUpdate) {
        pendForm = {
          ...pendForm,
          description: "Updating Contact",
        };
      }

      addPending(pendForm);

      const svcs = new Services();
      const contactRepo = await svcs.ContactRepository();
      const signUpRepo = await svcs.SignUpRepository();

      if (isUpdate) {
        let fields = getFields();

        if (!form.companyId) {
          throw new Error("Invalid Company ID");
        }

        if (!form.id) {
          throw new Error("Invalid Contact ID");
        }

        await contactRepo.update(token, form.companyId, form.id, fields);
        snackbarService.success(`User '${form.first} ${form.last}' updated`);
      } else {
        const contact = await contactRepo.add(token, form);
        await signUpRepo.CompleteContactSignUp(token, contact);
        snackbarService.success(`User '${form.first} ${form.last}' added`);
        history.push(returnUrl as string);
      }

      completePending(pendingForm);
    } catch (err) {
      completePending(pendForm);
      snackbarService.error(err);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormTextField<IContactForm> name="first" label="First Name" onChange={handleChange} formErrors={formErrors} form={form} required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField<IContactForm> name="last" label="Last Name" onChange={handleChange} form={form} formErrors={formErrors} required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField<IContactForm> name="title" label="Title" onChange={handleChange} form={form} formErrors={formErrors} required />
      </Grid>
      <Grid item xs={12}>
        <TextField name="companyName" label="Company" value={company!.Name} disabled fullWidth />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField<IContactForm> name="email" label="Email Address" onChange={handleChange} form={form} formErrors={formErrors} required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField<IContactForm> name="phone" label="Phone Number" onChange={handleChange} form={form} formErrors={formErrors} required />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox color="primary" name="contactManager" onChange={handleCheck} checked={form.contactManager} value={form.contactManager} />}
          label="Can Create/Edit Company Contacts"
        />
      </Grid>
      <ActionGrid item xs={12}>
        <ActionButton variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </ActionButton>
        <ActionButton variant="outlined" onClick={handleSubmit}>
          {isUpdate ? "Update" : "Create"}
        </ActionButton>
      </ActionGrid>
    </Grid>
  );
};

const FormWithState = connect(
  (state: IApplicationState) => ({
    ...selectAuth(state),
    company: selectCompany(state),
  }),
  dispatch => selectStatusDispatch(dispatch),
)(ContactAdd);

export default withRouter(FormWithState);
